import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  Cloud, DollarSign, Activity, AlertTriangle,
  Clock, Zap, RefreshCw, Save, CheckCircle,
  TrendingUp, Server, FileText, Settings,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const PAGE_FADE = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const SERVICE_COLORS = [
  "#3B82F6", "#6366F1", "#8B5CF6", "#EC4899",
  "#F59E0B", "#10B981", "#14B8A6", "#F97316",
];

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function GlassCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 ${className}`}
    >
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = "text-blue-400" }) {
  return (
    <GlassCard className="flex items-start gap-4">
      <div className={`p-3 rounded-lg bg-white/5 ${color}`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-zinc-400 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-semibold text-white mt-0.5 truncate">{value}</p>
        {sub && <p className="text-xs text-zinc-500 mt-0.5">{sub}</p>}
      </div>
    </GlassCard>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-white">{title}</h3>
      {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300 flex items-start gap-3">
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <RefreshCw size={20} className="animate-spin text-blue-400" />
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
      <Cloud size={32} className="mb-3 opacity-40" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

function formatCost(cost, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(cost);
}

function formatMonth(yyyymm) {
  if (!yyyymm || yyyymm.length < 6) return yyyymm;
  const y = yyyymm.slice(0, 4);
  const m = parseInt(yyyymm.slice(4, 6), 10);
  return new Date(y, m - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
}

// ---------------------------------------------------------------------------
// Tab: Billing
// ---------------------------------------------------------------------------

function BillingTab({ token }) {
  const [summary, setSummary] = useState(null);
  const [byService, setByService] = useState([]);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sumRes, svcRes, dayRes] = await Promise.all([
        axios.get(`${BACKEND}/api/cloud/billing/summary`, { headers }),
        axios.get(`${BACKEND}/api/cloud/billing/by-service`, { headers }),
        axios.get(`${BACKEND}/api/cloud/billing/daily`, { headers }),
      ]);
      setSummary(sumRes.data);
      setByService(svcRes.data.services || []);
      setDaily(dayRes.data.daily || []);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="space-y-4">
      <ErrorBanner message={error} />
      <p className="text-xs text-zinc-500 text-center">
        Configure GCP credentials in the <strong className="text-zinc-400">GCP Settings</strong> tab.
      </p>
    </div>
  );

  const currency = summary?.currency || "USD";
  const topService = byService[0]?.service_name || "—";

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={DollarSign}
          label="MTD Total Cost"
          value={summary ? formatCost(summary.total_cost, currency) : "—"}
          sub={summary ? formatMonth(summary.billing_month) : ""}
          color="text-blue-400"
        />
        <StatCard
          icon={TrendingUp}
          label="Billing Month"
          value={summary ? formatMonth(summary.billing_month) : "—"}
          color="text-indigo-400"
        />
        <StatCard
          icon={Server}
          label="Top Service"
          value={topService}
          sub={byService[0] ? formatCost(byService[0].total_cost, currency) : ""}
          color="text-violet-400"
        />
        <StatCard
          icon={Activity}
          label="Services Tracked"
          value={byService.length}
          sub="with non-zero cost this month"
          color="text-pink-400"
        />
      </div>

      {/* Cost by service */}
      <GlassCard>
        <SectionHeader
          title="Cost by GCP Service"
          subtitle={`Month-to-date · ${currency}`}
        />
        {byService.length === 0 ? (
          <EmptyState message="No billing data found for this month" />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={byService}
              layout="vertical"
              margin={{ left: 8, right: 24, top: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fill: "#71717a", fontSize: 11 }}
                tickFormatter={(v) => `${currency} ${v.toFixed(2)}`}
              />
              <YAxis
                type="category"
                dataKey="service_name"
                tick={{ fill: "#a1a1aa", fontSize: 11 }}
                width={180}
              />
              <Tooltip
                contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, color: "#fff" }}
                formatter={(v) => [formatCost(v, currency), "Cost"]}
              />
              <Bar dataKey="total_cost" radius={[0, 4, 4, 0]}>
                {byService.map((_, i) => (
                  <Cell key={i} fill={SERVICE_COLORS[i % SERVICE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </GlassCard>

      {/* Daily cost trend */}
      <GlassCard>
        <SectionHeader
          title="Daily Cost Trend"
          subtitle="Current month · area chart"
        />
        {daily.length === 0 ? (
          <EmptyState message="No daily cost data available" />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={daily} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis
                dataKey="date"
                tick={{ fill: "#71717a", fontSize: 11 }}
                tickFormatter={(d) => d.slice(5)}
              />
              <YAxis
                tick={{ fill: "#71717a", fontSize: 11 }}
                tickFormatter={(v) => `$${v.toFixed(2)}`}
              />
              <Tooltip
                contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, color: "#fff" }}
                formatter={(v) => [formatCost(v, currency), "Cost"]}
              />
              <Area
                type="monotone"
                dataKey="total_cost"
                stroke="#3B82F6"
                strokeWidth={2}
                fill="url(#costGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </GlassCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Document AI
// ---------------------------------------------------------------------------

function DocumentAITab({ token }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${BACKEND}/api/cloud/monitoring/documentai`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMetrics(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="space-y-4">
      <ErrorBanner message={error} />
      <p className="text-xs text-zinc-500 text-center">
        Ensure the Document AI API is enabled and credentials are configured in the{" "}
        <strong className="text-zinc-400">GCP Settings</strong> tab.
      </p>
    </div>
  );

  const daily = metrics?.daily_counts || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={FileText}
          label="Total Requests (MTD)"
          value={(metrics?.total_requests ?? 0).toLocaleString()}
          sub="process_document_count"
          color="text-blue-400"
        />
        <StatCard
          icon={Clock}
          label="Avg Latency"
          value={`${metrics?.avg_latency_ms ?? 0} ms`}
          sub="process_document_latencies"
          color="text-indigo-400"
        />
        <StatCard
          icon={Zap}
          label="P99 Latency"
          value={`${metrics?.p99_latency_ms ?? 0} ms`}
          sub="99th percentile"
          color="text-violet-400"
        />
        <StatCard
          icon={AlertTriangle}
          label="Errors (MTD)"
          value={(metrics?.error_count ?? 0).toLocaleString()}
          sub="non-OK status responses"
          color="text-red-400"
        />
      </div>

      <GlassCard>
        <SectionHeader
          title="Daily Document Processing"
          subtitle="Request count per day · current month"
        />
        {daily.length === 0 ? (
          <EmptyState message="No Document AI requests recorded this month" />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={daily} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis
                dataKey="date"
                tick={{ fill: "#71717a", fontSize: 11 }}
                tickFormatter={(d) => d.slice(5)}
              />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, color: "#fff" }}
                formatter={(v) => [v.toLocaleString(), "Requests"]}
              />
              <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </GlassCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: GCP Settings
// ---------------------------------------------------------------------------

function GCPSettingsTab({ token }) {
  const [form, setForm] = useState({
    project_id: "",
    billing_dataset: "",
    billing_table: "",
    credentials_json: "",
    enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/cloud/config`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        const d = r.data;
        setForm({
          project_id: d.project_id || "",
          billing_dataset: d.billing_dataset || "",
          billing_table: d.billing_table || "",
          credentials_json: d.credentials_json || "",
          enabled: d.enabled || false,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await axios.put(`${BACKEND}/api/cloud/config`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-2xl space-y-6">
      {error && <ErrorBanner message={error} />}

      <GlassCard>
        <SectionHeader title="GCP Project" subtitle="Core project settings for Billing and Monitoring" />
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">GCP Project ID</label>
            <input
              className="w-full rounded-lg bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
              placeholder="my-gcp-project-id"
              value={form.project_id}
              onChange={(e) => setForm({ ...form, project_id: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="gcp-enabled"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="w-4 h-4 rounded accent-blue-500"
            />
            <label htmlFor="gcp-enabled" className="text-sm text-zinc-300">
              Enable GCP Cloud Dashboard
            </label>
          </div>
        </div>
      </GlassCard>

      <GlassCard>
        <SectionHeader
          title="BigQuery Billing Export"
          subtitle="Configure where your Cloud Billing export is stored in BigQuery"
        />
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Dataset Name</label>
            <input
              className="w-full rounded-lg bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
              placeholder="billing_export"
              value={form.billing_dataset}
              onChange={(e) => setForm({ ...form, billing_dataset: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Table Name</label>
            <input
              className="w-full rounded-lg bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
              placeholder="gcp_billing_export_v1_XXXXXX_XXXXXX_XXXXXX"
              value={form.billing_table}
              onChange={(e) => setForm({ ...form, billing_table: e.target.value })}
            />
          </div>
          <p className="text-xs text-zinc-500">
            Enable billing export in{" "}
            <span className="text-zinc-400">
              GCP Console → Billing → Billing export → BigQuery export
            </span>
            {" "}then paste the dataset and table names above.
          </p>
        </div>
      </GlassCard>

      <GlassCard>
        <SectionHeader
          title="Service Account Credentials"
          subtitle="Paste the contents of your service account JSON key file"
        />
        <div className="space-y-2">
          <textarea
            rows={8}
            className="w-full rounded-lg bg-zinc-900 border border-zinc-700 px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500 font-mono resize-none"
            placeholder={'{\n  "type": "service_account",\n  "project_id": "...",\n  ...\n}'}
            value={form.credentials_json}
            onChange={(e) => setForm({ ...form, credentials_json: e.target.value })}
          />
          <p className="text-xs text-zinc-500">
            Leave empty to use the{" "}
            <span className="text-zinc-400">GOOGLE_APPLICATION_CREDENTIALS</span> env var or
            Application Default Credentials (ADC).
          </p>
        </div>
      </GlassCard>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
        >
          {saving ? (
            <RefreshCw size={14} className="animate-spin" />
          ) : saved ? (
            <CheckCircle size={14} />
          ) : (
            <Save size={14} />
          )}
          {saving ? "Saving…" : saved ? "Saved!" : "Save Configuration"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TABS = [
  { id: "billing", label: "Billing", icon: DollarSign },
  { id: "documentai", label: "Document AI", icon: Activity },
  { id: "settings", label: "GCP Settings", icon: Settings },
];

export default function CloudDashboardPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("billing");

  return (
    <motion.div {...PAGE_FADE} className="min-h-screen bg-zinc-950 text-white p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-400">
          <Cloud size={22} />
        </div>
        <div>
          <h1 className="text-xl font-semibold">Cloud Dashboard</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Google Cloud Billing &amp; Document AI metrics
          </p>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 p-1 rounded-xl bg-zinc-900 border border-zinc-800 w-fit mb-8">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === id
                ? "bg-blue-600 text-white"
                : "text-zinc-400 hover:text-white hover:bg-white/5"
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === "billing" && <BillingTab token={token} />}
        {activeTab === "documentai" && <DocumentAITab token={token} />}
        {activeTab === "settings" && <GCPSettingsTab token={token} />}
      </div>
    </motion.div>
  );
}
